import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  ChevronLeft, Plus, Trash2, Edit2, Syringe, Search, X, Check, ChevronDown,
  AlertTriangle, Calendar, Link2, Unlink, FileText, Activity
} from 'lucide-react';
import { toast } from 'sonner';
import TimelineButton from '../components/TimelineButton';
import { AttachmentSection } from '../components/AttachmentSection';
import { todayLocal, formatLocalDate } from '../utils/dateUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VaccineTracker = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [vaccines, setVaccines] = useState([]);
  const [photosOpenFor, setPhotosOpenFor] = useState({});
  const [vaccineOptions, setVaccineOptions] = useState({
    childhood_vaccines: [],
    flu_vaccines: [],
    covid_vaccines: [],
    common_reactions: []
  });
  const [symptomEntries, setSymptomEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [editingVaccine, setEditingVaccine] = useState(null);
  const [linkingVaccine, setLinkingVaccine] = useState(null);
  const [selectedType, setSelectedType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showVaccineDropdown, setShowVaccineDropdown] = useState(false);
  
  const [formData, setFormData] = useState({
    vaccine_name: '',
    vaccine_type: 'childhood',
    date_administered: todayLocal(),
    provider_name: '',
    provider_location: '',
    lot_number: '',
    reactions: [],
    reaction_severity: null,
    reaction_onset: '',
    notes: ''
  });

  const vaccineTypes = [
    { value: 'childhood', label: 'Childhood Vaccine' },
    { value: 'flu', label: 'Flu Vaccine' },
    { value: 'covid', label: 'COVID-19 Vaccine' },
    { value: 'other', label: 'Other' }
  ];

  const reactionOnsets = [
    { value: 'immediate', label: 'Immediate (within minutes)' },
    { value: 'hours', label: 'Within hours' },
    { value: 'days', label: 'Within days' },
    { value: 'weeks', label: 'Within weeks' }
  ];

  useEffect(() => {
    fetchVaccines();
    fetchVaccineOptions();
    fetchSymptomEntries();
  }, [token]);

  const fetchVaccines = async () => {
    try {
      const response = await fetch(`${API_URL}/api/vaccines`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setVaccines(data || []);
    } catch (error) {
      console.error('Error fetching vaccines:', error);
      toast.error('Failed to load vaccines');
    } finally {
      setLoading(false);
    }
  };

  const fetchVaccineOptions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/vaccines/vaccine-options`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setVaccineOptions(data);
    } catch (error) {
      console.error('Error fetching vaccine options:', error);
    }
  };

  const fetchSymptomEntries = async () => {
    try {
      const response = await fetch(`${API_URL}/api/symptoms`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSymptomEntries(data || []);
    } catch (error) {
      console.error('Error fetching symptom entries:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.vaccine_name) {
      toast.error('Please select or enter a vaccine name');
      return;
    }
    
    try {
      const url = editingVaccine 
        ? `${API_URL}/api/vaccines/${editingVaccine.id}`
        : `${API_URL}/api/vaccines`;
      
      const response = await fetch(url, {
        method: editingVaccine ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingVaccine ? 'Vaccine record updated' : 'Vaccine record added');
        setShowAddModal(false);
        setEditingVaccine(null);
        resetForm();
        fetchVaccines();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error saving vaccine record');
      }
    } catch (error) {
      toast.error('Error saving vaccine record');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this vaccine record?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/vaccines/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Vaccine record deleted');
        fetchVaccines();
      }
    } catch (error) {
      toast.error('Error deleting vaccine record');
    }
  };

  const handleLinkSymptom = async (symptomId) => {
    if (!linkingVaccine) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/vaccines/${linkingVaccine.id}/link-symptom/${symptomId}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast.success('Symptom entry linked to vaccine');
        fetchVaccines();
      }
    } catch (error) {
      toast.error('Error linking symptom');
    }
  };

  const handleUnlinkSymptom = async (vaccineId, symptomId) => {
    try {
      const response = await fetch(
        `${API_URL}/api/vaccines/${vaccineId}/link-symptom/${symptomId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast.success('Symptom entry unlinked');
        fetchVaccines();
      }
    } catch (error) {
      toast.error('Error unlinking symptom');
    }
  };

  const resetForm = () => {
    setFormData({
      vaccine_name: '',
      vaccine_type: 'childhood',
      date_administered: todayLocal(),
      provider_name: '',
      provider_location: '',
      lot_number: '',
      reactions: [],
      reaction_severity: null,
      reaction_onset: '',
      notes: ''
    });
  };

  const openEditModal = (vaccine) => {
    setEditingVaccine(vaccine);
    setFormData({
      vaccine_name: vaccine.vaccine_name,
      vaccine_type: vaccine.vaccine_type,
      date_administered: vaccine.date_administered,
      provider_name: vaccine.provider_name || '',
      provider_location: vaccine.provider_location || '',
      lot_number: vaccine.lot_number || '',
      reactions: vaccine.reactions || [],
      reaction_severity: vaccine.reaction_severity,
      reaction_onset: vaccine.reaction_onset || '',
      notes: vaccine.notes || ''
    });
    setShowAddModal(true);
  };

  const openLinkModal = (vaccine) => {
    setLinkingVaccine(vaccine);
    setShowLinkModal(true);
  };

  const toggleReaction = (reaction) => {
    setFormData(prev => ({
      ...prev,
      reactions: prev.reactions.includes(reaction)
        ? prev.reactions.filter(r => r !== reaction)
        : [...prev.reactions, reaction]
    }));
  };

  const getVaccineListForType = () => {
    switch (formData.vaccine_type) {
      case 'childhood':
        return vaccineOptions.childhood_vaccines;
      case 'flu':
        return vaccineOptions.flu_vaccines;
      case 'covid':
        return vaccineOptions.covid_vaccines;
      default:
        return [];
    }
  };

  const filteredVaccines = vaccines.filter(v => {
    const matchesType = selectedType === 'all' || v.vaccine_type === selectedType;
    const matchesSearch = v.vaccine_name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesType && matchesSearch;
  });

  const getLinkedSymptoms = (vaccineId) => {
    const vaccine = vaccines.find(v => v.id === vaccineId);
    if (!vaccine?.linked_symptom_entries?.length) return [];
    return symptomEntries.filter(s => vaccine.linked_symptom_entries.includes(s.id));
  };

  const getUnlinkedSymptoms = () => {
    if (!linkingVaccine) return symptomEntries;
    return symptomEntries.filter(s => !linkingVaccine.linked_symptom_entries?.includes(s.id));
  };

  const formatDate = (dateStr) => {
    return formatLocalDate(dateStr, 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDF8F4]">
        <div className="text-center">
          <Syringe className="w-16 h-16 text-primary mx-auto mb-4 animate-pulse" />
          <p className="text-[#A8A29E]">Loading vaccine records...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDF8F4]">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-primary/80 text-white p-6">
        <div className="max-w-4xl mx-auto">
          <button 
            onClick={() => navigate('/dashboard')}
            className="flex items-center text-white/80 hover:text-white mb-4"
            data-testid="back-button"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Back to Dashboard
          </button>
          <div className="flex items-center gap-3">
            <Syringe className="w-8 h-8" />
            <div>
              <h1 className="text-2xl font-nunito font-bold">Vaccine Tracker</h1>
              <p className="text-white/80 text-sm">Track vaccinations and monitor for PANDAS/PANS reactions</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-4">
        {/* Info Card */}
        <Card className="p-4 bg-amber-50 border-amber-200">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-semibold mb-1">PANDAS/PANS & Vaccines</p>
              <p>Some children with PANDAS/PANS may experience symptom flares after vaccinations. 
              Track reactions and link them to symptom entries to identify patterns and share with your care team.</p>
            </div>
          </div>
        </Card>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <TimelineButton className="sm:w-auto" />
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search vaccines..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="vaccine-search"
            />
          </div>
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="px-4 py-2 rounded-lg border border-gray-200 bg-white"
            data-testid="vaccine-type-filter"
          >
            <option value="all">All Types</option>
            {vaccineTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
          <Button 
            onClick={() => { resetForm(); setEditingVaccine(null); setShowAddModal(true); }}
            className="bg-primary hover:bg-primary/90"
            data-testid="add-vaccine-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Vaccine
          </Button>
        </div>

        {/* Vaccine List */}
        {filteredVaccines.length === 0 ? (
          <Card className="p-8 text-center">
            <Syringe className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="font-semibold text-gray-600 mb-1">No vaccine records yet</h3>
            <p className="text-sm text-gray-500 mb-4">Start tracking your child's vaccinations to monitor for reactions</p>
            <Button onClick={() => setShowAddModal(true)} className="bg-primary">
              <Plus className="w-4 h-4 mr-2" />
              Add First Vaccine
            </Button>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredVaccines.map(vaccine => {
              const linkedSymptoms = getLinkedSymptoms(vaccine.id);
              return (
                <Card key={vaccine.id} className="p-4" data-testid={`vaccine-card-${vaccine.id}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-[#2D2D2D]">{vaccine.vaccine_name}</h3>
                        {vaccine.reactions?.length > 0 && (
                          <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            Reaction
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {formatDate(vaccine.date_administered)}
                        </span>
                        <span className="capitalize">{vaccine.vaccine_type.replace('_', ' ')}</span>
                        {vaccine.provider_name && <span>{vaccine.provider_name}</span>}
                        {vaccine.lot_number && <span>Lot: {vaccine.lot_number}</span>}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button 
                        onClick={() => openLinkModal(vaccine)}
                        className="p-2 hover:bg-gray-100 rounded-lg text-gray-500"
                        title="Link symptom entries"
                        data-testid={`link-symptoms-${vaccine.id}`}
                      >
                        <Link2 className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => openEditModal(vaccine)}
                        className="p-2 hover:bg-gray-100 rounded-lg text-gray-500"
                        data-testid={`edit-vaccine-${vaccine.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleDelete(vaccine.id)}
                        className="p-2 hover:bg-red-50 rounded-lg text-red-500"
                        data-testid={`delete-vaccine-${vaccine.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Reactions */}
                  {vaccine.reactions?.length > 0 && (
                    <div className="mb-3 p-3 bg-red-50 rounded-lg">
                      <p className="text-sm font-medium text-red-800 mb-2">Reactions:</p>
                      <div className="flex flex-wrap gap-1">
                        {vaccine.reactions.map((reaction, idx) => (
                          <span key={idx} className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded">
                            {reaction}
                          </span>
                        ))}
                      </div>
                      {vaccine.reaction_severity && (
                        <p className="text-xs text-red-600 mt-2">
                          Severity: {vaccine.reaction_severity}/10
                          {vaccine.reaction_onset && ` • Onset: ${vaccine.reaction_onset}`}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Linked Symptoms */}
                  {linkedSymptoms.length > 0 && (
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm font-medium text-blue-800 mb-2 flex items-center gap-1">
                        <Activity className="w-4 h-4" />
                        Linked Symptom Entries:
                      </p>
                      <div className="space-y-1">
                        {linkedSymptoms.map(symptom => (
                          <div key={symptom.id} className="flex items-center justify-between text-sm">
                            <span className="text-blue-700">
                              {formatDate(symptom.date)} - Severity: {symptom.severity}/10
                              {symptom.symptoms?.length > 0 && ` (${symptom.symptoms.slice(0, 2).join(', ')}...)`}
                            </span>
                            <button
                              onClick={() => handleUnlinkSymptom(vaccine.id, symptom.id)}
                              className="text-blue-600 hover:text-blue-800 p-1"
                              title="Unlink symptom"
                            >
                              <Unlink className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  {vaccine.notes && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">Notes:</span> {vaccine.notes}
                      </p>
                    </div>
                  )}

                  <div className="mt-3 pt-3 border-t border-stone-100">
                    {photosOpenFor[vaccine.id] ? (
                      <AttachmentSection
                        parentType="vaccine"
                        parentId={vaccine.id}
                        token={token}
                        title="Vaccine Card Photos"
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => setPhotosOpenFor(prev => ({ ...prev, [vaccine.id]: true }))}
                        className="text-xs text-primary hover:underline flex items-center gap-1"
                        data-testid={`open-photos-${vaccine.id}`}
                      >
                        <Plus className="w-3 h-3" /> Attach vaccine card photo
                      </button>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b sticky top-0 bg-white z-10">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold">
                  {editingVaccine ? 'Edit Vaccine Record' : 'Add Vaccine Record'}
                </h2>
                <button 
                  onClick={() => { setShowAddModal(false); setEditingVaccine(null); resetForm(); }}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              {/* Vaccine Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vaccine Type *</label>
                <select
                  value={formData.vaccine_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, vaccine_type: e.target.value, vaccine_name: '' }))}
                  className="w-full px-3 py-2 border rounded-lg"
                  data-testid="vaccine-type-select"
                >
                  {vaccineTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              {/* Vaccine Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vaccine Name *</label>
                {formData.vaccine_type !== 'other' ? (
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowVaccineDropdown(!showVaccineDropdown)}
                      className="w-full px-3 py-2 border rounded-lg text-left flex items-center justify-between bg-white"
                      data-testid="vaccine-name-dropdown"
                    >
                      <span className={formData.vaccine_name ? 'text-gray-900' : 'text-gray-400'}>
                        {formData.vaccine_name || 'Select a vaccine...'}
                      </span>
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    {showVaccineDropdown && (
                      <div className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {getVaccineListForType().map(vaccine => (
                          <button
                            key={vaccine}
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({ ...prev, vaccine_name: vaccine }));
                              setShowVaccineDropdown(false);
                            }}
                            className="w-full px-3 py-2 text-left hover:bg-gray-50 text-sm flex items-center justify-between"
                          >
                            {vaccine}
                            {formData.vaccine_name === vaccine && <Check className="w-4 h-4 text-primary" />}
                          </button>
                        ))}
                        <div className="border-t p-2">
                          <Input
                            placeholder="Or type custom vaccine name..."
                            value={formData.vaccine_name}
                            onChange={(e) => setFormData(prev => ({ ...prev, vaccine_name: e.target.value }))}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Input
                    placeholder="Enter vaccine name"
                    value={formData.vaccine_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, vaccine_name: e.target.value }))}
                    data-testid="vaccine-name-input"
                  />
                )}
              </div>

              {/* Date Administered */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date Administered *</label>
                <Input
                  type="date"
                  value={formData.date_administered}
                  onChange={(e) => setFormData(prev => ({ ...prev, date_administered: e.target.value }))}
                  data-testid="vaccine-date"
                />
              </div>

              {/* Provider Info */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Provider Name</label>
                  <Input
                    placeholder="Dr. Smith"
                    value={formData.provider_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, provider_name: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <Input
                    placeholder="City Pediatrics"
                    value={formData.provider_location}
                    onChange={(e) => setFormData(prev => ({ ...prev, provider_location: e.target.value }))}
                  />
                </div>
              </div>

              {/* Lot Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lot Number</label>
                <Input
                  placeholder="Enter lot number from vaccine card"
                  value={formData.lot_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, lot_number: e.target.value }))}
                />
              </div>

              {/* Reactions Section */}
              <div className="border-t pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reactions / Side Effects
                </label>
                <p className="text-xs text-gray-500 mb-3">Select any reactions observed after vaccination</p>
                <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-2 bg-gray-50 rounded-lg">
                  {vaccineOptions.common_reactions.map(reaction => (
                    <button
                      key={reaction}
                      type="button"
                      onClick={() => toggleReaction(reaction)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                        formData.reactions.includes(reaction)
                          ? 'bg-red-500 text-white'
                          : 'bg-white border text-gray-600 hover:border-red-300'
                      }`}
                    >
                      {reaction}
                    </button>
                  ))}
                </div>
              </div>

              {/* Reaction Details (shown if reactions selected) */}
              {formData.reactions.length > 0 && (
                <div className="space-y-3 p-3 bg-red-50 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-red-800 mb-1">Reaction Severity</label>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                        <button
                          key={num}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, reaction_severity: num }))}
                          className={`w-8 h-8 rounded text-sm font-medium ${
                            formData.reaction_severity === num
                              ? 'bg-red-500 text-white'
                              : 'bg-white border hover:border-red-300'
                          }`}
                        >
                          {num}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-red-600 mt-1">1 = Mild, 10 = Severe</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-red-800 mb-1">When did reactions start?</label>
                    <select
                      value={formData.reaction_onset}
                      onChange={(e) => setFormData(prev => ({ ...prev, reaction_onset: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="">Select onset time...</option>
                      {reactionOnsets.map(onset => (
                        <option key={onset.value} value={onset.value}>{onset.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  placeholder="Any additional observations or notes..."
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg resize-none"
                  rows={3}
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowAddModal(false); setEditingVaccine(null); resetForm(); }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-primary" data-testid="save-vaccine-btn">
                  {editingVaccine ? 'Update' : 'Save'} Record
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* Link Symptoms Modal */}
      {showLinkModal && linkingVaccine && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b sticky top-0 bg-white z-10">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-lg font-semibold">Link Symptom Entries</h2>
                  <p className="text-sm text-gray-500">{linkingVaccine.vaccine_name}</p>
                </div>
                <button 
                  onClick={() => { setShowLinkModal(false); setLinkingVaccine(null); }}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-4">
              <p className="text-sm text-gray-600 mb-4">
                Link symptom tracker entries that may be related to this vaccination. 
                This helps track potential PANDAS/PANS flares following vaccines.
              </p>
              
              {getUnlinkedSymptoms().length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No symptom entries available to link</p>
                  <p className="text-sm">Add entries in the Symptom Tracker first</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {getUnlinkedSymptoms()
                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                    .map(symptom => (
                      <button
                        key={symptom.id}
                        onClick={() => handleLinkSymptom(symptom.id)}
                        className="w-full p-3 border rounded-lg text-left hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{formatDate(symptom.date)}</p>
                            <p className="text-sm text-gray-600">
                              Severity: {symptom.severity}/10
                              {symptom.symptoms?.length > 0 && (
                                <span className="ml-2">• {symptom.symptoms.slice(0, 3).join(', ')}</span>
                              )}
                            </p>
                          </div>
                          <Link2 className="w-4 h-4 text-gray-400" />
                        </div>
                      </button>
                    ))}
                </div>
              )}
              
              <div className="mt-4 pt-4 border-t">
                <Button
                  onClick={() => { setShowLinkModal(false); setLinkingVaccine(null); }}
                  className="w-full"
                  variant="outline"
                >
                  Done
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default VaccineTracker;
